import types from '../mutation-types';

import { REPLY_EDITOR_MODES } from 'dashboard/components/widgets/WootWriter/constants';
import { LocalStorage } from 'shared/helpers/localStorage';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';

const state = {
  records: LocalStorage.get(LOCAL_STORAGE_KEYS.DRAFT_MESSAGES) || {},
  // Per draft key, `{ accountId, signature }`: the signature an accepted AI
  // email draft is signed with instead of the nurse's own
  // (ai-docs/ai-suggestions.md "Email signature"). Stored and removed with
  // the draft, so it survives a reload as long as the draft does.
  signatures: LocalStorage.get(LOCAL_STORAGE_KEYS.DRAFT_SIGNATURES) || {},
  // Per draft key, `{ accountId, suggestionId }`: the AI suggestion whose
  // draft the dock put in this reply. Only such a reply names the suggestion
  // when it is sent (content_attributes ai_suggestion_id), so the server checks
  // the draft is still allowed and records an AI outcome for it
  // (ai-docs/ai-suggestions.md "Send"). Stored and removed with the draft.
  aiSuggestions:
    LocalStorage.get(LOCAL_STORAGE_KEYS.DRAFT_AI_SUGGESTIONS) || {},
  // Per draft key, `{ accountId }`: a refused reply came back into this draft
  // without the attachments it was sent with (ReplyBox giveBackRevokedReply),
  // so the reply box says so and holds Send until the nurse edits the reply,
  // adds a file or chooses to send without them. Stored and removed with the
  // draft.
  droppedAttachments:
    LocalStorage.get(LOCAL_STORAGE_KEYS.DRAFT_DROPPED_ATTACHMENTS) || {},
  replyEditorMode: REPLY_EDITOR_MODES.REPLY,
};

export const getters = {
  get: _state => key => {
    return _state.records[key] || '';
  },
  getReplyEditorMode: _state => _state.replyEditorMode,
  getSignature: _state => (key, accountId) => {
    const entry = (_state.signatures || {})[key];
    if (!entry || String(entry.accountId) !== String(accountId)) return null;
    return entry.signature || null;
  },
  getAiSuggestion: _state => (key, accountId) => {
    const entry = (_state.aiSuggestions || {})[key];
    if (!entry || String(entry.accountId) !== String(accountId)) return null;
    return entry.suggestionId || null;
  },
  hasDroppedAttachments: _state => (key, accountId) => {
    const entry = (_state.droppedAttachments || {})[key];
    return !!entry && String(entry.accountId) === String(accountId);
  },
};

export const actions = {
  set: async ({ commit }, { key, message }) => {
    commit(types.SET_DRAFT_MESSAGES, { key, message });
  },
  delete: ({ commit }, { key }) => {
    commit(types.REMOVE_DRAFT_MESSAGES, { key });
  },
  // A blank signature removes it: the draft goes back to the nurse's own.
  setSignature: ({ commit }, { key, accountId, signature }) => {
    commit(types.SET_DRAFT_SIGNATURE, { key, accountId, signature });
  },
  // A blank id removes it: the reply no longer holds an AI draft.
  setAiSuggestion: ({ commit }, { key, accountId, suggestionId }) => {
    commit(types.SET_DRAFT_AI_SUGGESTION, { key, accountId, suggestionId });
  },
  // `dropped: false` removes the mark.
  setDroppedAttachments: ({ commit }, { key, accountId, dropped }) => {
    commit(types.SET_DRAFT_DROPPED_ATTACHMENTS, { key, accountId, dropped });
  },
  setReplyEditorMode: ({ commit }, { mode }) => {
    commit(types.SET_REPLY_EDITOR_MODE, { mode });
  },
};

export const mutations = {
  [types.SET_DRAFT_MESSAGES]($state, { key, message }) {
    $state.records = {
      ...$state.records,
      [key]: message,
    };
    LocalStorage.set(LOCAL_STORAGE_KEYS.DRAFT_MESSAGES, $state.records);
  },
  [types.REMOVE_DRAFT_MESSAGES]($state, { key }) {
    const { [key]: draftToBeRemoved, ...updatedRecords } = $state.records;
    $state.records = updatedRecords;
    LocalStorage.set(LOCAL_STORAGE_KEYS.DRAFT_MESSAGES, $state.records);
    if ($state.signatures?.[key]) {
      const { [key]: signatureToBeRemoved, ...signatures } = $state.signatures;
      $state.signatures = signatures;
      LocalStorage.set(LOCAL_STORAGE_KEYS.DRAFT_SIGNATURES, $state.signatures);
    }
    if ($state.aiSuggestions?.[key]) {
      const { [key]: linkToBeRemoved, ...aiSuggestions } = $state.aiSuggestions;
      $state.aiSuggestions = aiSuggestions;
      LocalStorage.set(
        LOCAL_STORAGE_KEYS.DRAFT_AI_SUGGESTIONS,
        $state.aiSuggestions
      );
    }
    if ($state.droppedAttachments?.[key]) {
      const { [key]: markToBeRemoved, ...droppedAttachments } =
        $state.droppedAttachments;
      $state.droppedAttachments = droppedAttachments;
      LocalStorage.set(
        LOCAL_STORAGE_KEYS.DRAFT_DROPPED_ATTACHMENTS,
        $state.droppedAttachments
      );
    }
  },
  [types.SET_DRAFT_SIGNATURE]($state, { key, accountId, signature }) {
    const { [key]: previous, ...signatures } = $state.signatures || {};
    $state.signatures = signature
      ? { ...signatures, [key]: { accountId, signature } }
      : signatures;
    LocalStorage.set(LOCAL_STORAGE_KEYS.DRAFT_SIGNATURES, $state.signatures);
  },
  [types.SET_DRAFT_AI_SUGGESTION]($state, { key, accountId, suggestionId }) {
    const { [key]: previous, ...aiSuggestions } = $state.aiSuggestions || {};
    $state.aiSuggestions = suggestionId
      ? { ...aiSuggestions, [key]: { accountId, suggestionId } }
      : aiSuggestions;
    LocalStorage.set(
      LOCAL_STORAGE_KEYS.DRAFT_AI_SUGGESTIONS,
      $state.aiSuggestions
    );
  },
  [types.SET_DRAFT_DROPPED_ATTACHMENTS]($state, { key, accountId, dropped }) {
    const { [key]: previous, ...droppedAttachments } =
      $state.droppedAttachments || {};
    $state.droppedAttachments = dropped
      ? { ...droppedAttachments, [key]: { accountId } }
      : droppedAttachments;
    LocalStorage.set(
      LOCAL_STORAGE_KEYS.DRAFT_DROPPED_ATTACHMENTS,
      $state.droppedAttachments
    );
  },
  [types.SET_REPLY_EDITOR_MODE]($state, { mode }) {
    $state.replyEditorMode = mode;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
